import { useMemo } from 'react';
import { ColumnFilters } from './useTableFilters';

export interface UseSpreadsheetDataOptions<T extends { _id: string }> {
  data: T[];
  columnFilters?: ColumnFilters;
  filterFn?: (item: T, filters: ColumnFilters) => boolean;
}

export interface UseSpreadsheetDataReturn<T extends { _id: string }> {
  transformedData: Array<T & { id: string }>;
  filteredData: Array<T & { id: string }>;
}

/**
 * Hook for transforming and filtering data for Spreadsheet components
 * Adds `id` field (required by Spreadsheet) and applies column filters
 * 
 * @param options - Data, column filters, and optional custom filter function
 * @returns Transformed data (with id) and filtered data
 * 
 * @example
 * // Simple usage with default filtering
 * const { transformedData, filteredData } = useSpreadsheetData({
 *   data: users,
 *   columnFilters
 * });
 * 
 * @example
 * // With custom filter function
 * const { transformedData, filteredData } = useSpreadsheetData({
 *   data: places,
 *   columnFilters,
 *   filterFn: (place, filters) => {
 *     if (filters.type && !filters.type.includes(place.type)) return false;
 *     if (filters.status && !filters.status.includes(place.status)) return false;
 *     return true;
 *   }
 * });
 */
export const useSpreadsheetData = <T extends { _id: string }>({
  data,
  columnFilters = {},
  filterFn,
}: UseSpreadsheetDataOptions<T>): UseSpreadsheetDataReturn<T> => {
  // Transform data to include `id` field (required by Spreadsheet)
  const transformedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      id: item._id,
    }));
  }, [data]);

  // Apply column filters
  const filteredData = useMemo(() => {
    if (!columnFilters || Object.keys(columnFilters).length === 0) {
      return transformedData;
    }

    if (filterFn) {
      return transformedData.filter(item => filterFn(item, columnFilters));
    }

    // Default: no filtering (component should handle filtering)
    return transformedData;
  }, [transformedData, columnFilters, filterFn]);

  return {
    transformedData,
    filteredData,
  };
};
